import { useEffect, useState } from 'react'
import { Alert, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { com, kotlin } from '@eidu/form'
import EntityFieldDisplayGroup from '../shared/EntityFieldDisplayGroup'
import PageLayout from '../shared/PageLayout'
import { KindViewPageProps } from '../KindPages'
import FormContentInput from '../../../../components/input/FormContentInput'
import getFormContent from '../../../../io/api/form/getFormContent'
import LoadingOverlay from '../../../../components/feedback/LoadingOverlay'
import useHasPermission from '../../../permission/useHasPermission'
import getFormContentId = com.eidu.sharedlib.entity.getFormContentId
import KtList = kotlin.collections.KtList
import Page = com.eidu.sharedlib.form.definition.Page
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction

const FormViewPage = ({ entity, entityType, fields }: KindViewPageProps) => {
  const navigate = useNavigate()
  const entityId = entity.entity.id.toString()
  const formContentId = getFormContentId(entity.entity)?.toString()
  const [formContent, setFormContent] = useState<KtList<Page> | undefined>(undefined)
  const [formContentLoading, setFormContentLoading] = useState(!!formContentId)
  const [formContentError, setFormContentError] = useState<string>()

  const { hasPermission: hasUpdatePermission, loading: permissionsLoading } = useHasPermission(
    entity.entity.id,
    EntityAction.UpdateEntity
  )

  const navigateToEdit = () => {
    navigate(`/entities/${entityType.id.toString()}/edit/${entityId}`)
  }

  useEffect(() => {
    if (formContentId) {
      setFormContentLoading(true)
      setFormContentError(undefined)
      getFormContent({
        formId: entityId,
        formContentId,
      })
        .then((content) => {
          const formContentWithPageKeys = KtList.fromJsArray(
            content
              .asJsReadonlyArrayView()
              .map((page: Page, index: number) =>
                !page.key ? page.copy(page.title, page.items, `page_${Date.now()}_${index}`) : page
              )
          )
          setFormContent(formContentWithPageKeys)
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : String(err)
          setFormContentError(message)
          setFormContent(undefined)
          console.error(err)
        })
        .finally(() => setFormContentLoading(false))
    } else {
      setFormContent(KtList.fromJsArray([]))
      setFormContentLoading(false)
    }
  }, [entityId, formContentId])

  return (
    <>
      <PageLayout
        title={entity.primaryLabelText}
        subtitle={entityType.name}
        actions={hasUpdatePermission ? <Button onClick={navigateToEdit}>Edit</Button> : undefined}
        showBack
      >
        <EntityFieldDisplayGroup entityType={entityType} fields={fields} />
        {formContentError && <Alert severity="error">{formContentError}</Alert>}
        {formContent &&
          ((formContent.asJsReadonlyArrayView().every((page) => page.key) && (
            <FormContentInput pages={formContent} />
          )) ||
            (formContent.size > 0 && (
              <Alert severity={'error'}>
                Some pages on this form do not have a key set. This is not supported. Contact EIDU if the issue
                persists.
              </Alert>
            )))}
      </PageLayout>
      <LoadingOverlay isOpen={formContentLoading || permissionsLoading} />
    </>
  )
}

export default FormViewPage
